import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {FontIcon} from '@mintdata/mint-kit/impl/properties/components/fontIcon/FontIcon';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import * as classnames from 'classnames/bind';
import {css} from 'glamor';
import * as React from 'react';

const styles = require('@mintdata/mint-kit/impl/properties/components/binding/Binding.less');
const cx = classnames.bind(styles);

export type TargetElement = React.ReactElement<any>;

export enum IconPlace
{
    LeftBottom,
    RightBottom,
    CenterBottom,
    LeftTop,
    RightTop,
    CenterTop,
    LeftMiddle,
    RightMiddle,
}

export enum TooltipPlace
{
    Top,
    Right,
    Bottom,
    Left,
}

export interface IBindingState
{
    showBindPopover:boolean;
}

export interface IBindingProps
{
    className?:string;
    tooltipClassName?:string;

    target:TargetElement;
    isBound?:boolean;
    isError?:boolean;
    isSelected?:boolean;
    iconPlace?:IconPlace;
    tooltipPlace?:TooltipPlace;

    onBindClick?:(event:React.MouseEvent<HTMLElement>) => void;
    onUnBindClick?:(event:React.MouseEvent<HTMLElement>) => void;
    onSelect?:(event:React.MouseEvent<HTMLElement>) => void;
}

/**
 * TODO: Legacy binding icon implementation. Get rid of two identical icons.
 */
export class Binding extends React.Component<IBindingProps, IBindingState>
{
    public static defaultProps = {
        isBound: false,
        isSelected: false,
        iconPlace: IconPlace.LeftMiddle,
        tooltipPlace: TooltipPlace.Left
    };

    private _popoverId:string = IdUtil.newComponentId();

    private _bindingIconElement:HTMLElement;
    private _refHandlers = {
        bindingIcon: (el:HTMLElement) => (this._bindingIconElement = el)
    };

    constructor(props:IBindingProps)
    {
        super(props);

        this.state = {
            showBindPopover: false
        };
    }

    public render():JSX.Element
    {
        const {target, isBound, isSelected, iconPlace, className, isError} = this.props;
        const iconPlaceClass = StyleUtils.getBindingIconClass(iconPlace);

        let styleForChildrenBinding = {};

        if( isBound )
            styleForChildrenBinding = css({
                '&:hover [data-binding-target="true"] [data-binding-wrapper="true"]': {
                    visibility: 'hidden',
                    opacity: 0,
                },
                '&:hover [data-binding-target="true"] [data-binding-icon="true"]': {
                    visibility: 'hidden',
                    opacity: 0,
                },
            });

        return (
            <div
                className={cx('base', className)}
                {...styleForChildrenBinding}
            >
                <div className={cx('target')} data-binding-target={true}>
                    {target}
                </div>
                <div className={cx('bindingWrapper', {isBound, isSelected, isError})} data-binding-wrapper={true}>
                    <div
                        className={styles.bindingViewOpen}
                        onClick={this._handleSelect}
                    />
                    <div
                        ref={this._refHandlers.bindingIcon}
                        className={cx('bindingIcon', iconPlaceClass)}
                        onMouseEnter={this._openPopover}
                        onMouseLeave={this._closePopover}
                        data-binding-icon={true}
                    />
                    {this._renderPopover()}
                </div>
            </div>
        );
    }

    private _openPopover = () =>
    {
        this.setState({showBindPopover: true});
    };

    private _closePopover = () =>
    {
        this.setState({showBindPopover: false});
    };

    private _renderPopover()
    {
        const {showBindPopover} = this.state;
        const {isBound, tooltipPlace, tooltipClassName} = this.props;

        const tooltipPlaceClass = StyleUtils.getBindingTooltipClass(tooltipPlace);
        const sourceAnchor = StyleUtils.getSourceAnchor(tooltipPlace);
        const popupAnchor = StyleUtils.getPopupAnchor(tooltipPlace);

        return showBindPopover &&
            (
                <Popover
                    id={this._popoverId}
                    className={cx('bindingPopover', tooltipClassName)}
                    isModal={false}
                    popupSource={this._bindingIconElement}
                    sourceAnchor={sourceAnchor}
                    popupAnchor={popupAnchor}
                    onDismissed={this._closePopover}
                >
                    <div
                        className={cx('bindingPopoverContent', tooltipPlaceClass)}
                        onMouseEnter={this._openPopover}
                        onMouseLeave={this._closePopover}
                    >
                        {
                            isBound ? (
                                <div className={styles.unbindButton} onClick={this._handleUnbindClick}>
                                    <FontIcon iconName={'pt-icon-cross'} className={cx('icon')} />
                                    Remove Binding
                                    <div className={cx('bindingIcon')} />
                                </div>
                            ) : (
                                <div className={styles.bindButton} onClick={this._handleBindClick}>
                                    <FontIcon iconName={'pt-icon-th'} className={cx('icon')} />
                                    Bind to Data
                                    <div className={cx('bindingIcon')} />
                                </div>
                            )
                        }
                    </div>
                </Popover>
            );
    }

    private _handleBindClick = (event:React.MouseEvent<HTMLElement>) =>
    {
        const {onBindClick} = this.props;

        if( onBindClick )
        {
            onBindClick(event);
        }
    };

    private _handleUnbindClick = (event:React.MouseEvent<HTMLElement>) =>
    {
        const {onUnBindClick} = this.props;

        this._closePopover();

        if( onUnBindClick )
        {
            onUnBindClick(event);
        }
    };

    private _handleSelect = (event:React.MouseEvent<HTMLElement>) =>
    {
        const {onSelect} = this.props;

        if( onSelect )
        {
            onSelect(event);
        }
    };
}

class StyleUtils
{
    public static getBindingIconClass(iconPlace:IconPlace):string
    {
        switch( iconPlace )
        {
            case IconPlace.LeftBottom:
                return 'iconLeftBottom';
            case IconPlace.RightBottom:
                return 'iconRightBottom';
            case IconPlace.CenterBottom:
                return 'iconCenterBottom';
            case IconPlace.LeftTop:
                return 'iconLeftTop';
            case IconPlace.RightTop:
                return 'iconRightTop';
            case IconPlace.CenterTop:
                return 'iconCenterTop';
            case IconPlace.LeftMiddle:
                return 'iconLeftMiddle';
            case IconPlace.RightMiddle:
                return 'iconRightMiddle';
        }
    }

    public static getBindingTooltipClass(tooltipPlace:TooltipPlace):string
    {
        switch( tooltipPlace )
        {
            case TooltipPlace.Top:
                return 'tooltipTop';
            case TooltipPlace.Right:
                return 'tooltipRight';
            case TooltipPlace.Bottom:
                return 'tooltipBottom';
            case TooltipPlace.Left:
                return 'tooltipLeft';
        }
    }

    public static getSourceAnchor(tooltipPlace:TooltipPlace):SourceAnchor
    {
        switch( tooltipPlace )
        {
            case TooltipPlace.Top:
                return SourceAnchor.CenterBottom;
            case TooltipPlace.Right:
                return SourceAnchor.LeftTop;
            case TooltipPlace.Bottom:
                return SourceAnchor.CenterTop;
            case TooltipPlace.Left:
                return SourceAnchor.RightTop;
        }
    }

    public static getPopupAnchor(tooltipPlace:TooltipPlace):PopupAnchor
    {
        switch( tooltipPlace )
        {
            case TooltipPlace.Top:
                return PopupAnchor.CenterBottom;
            case TooltipPlace.Right:
                return PopupAnchor.LeftTop;
            case TooltipPlace.Bottom:
                return PopupAnchor.CenterTop;
            case TooltipPlace.Left:
                return PopupAnchor.RightTop;
        }
    }
}

